import React, { useEffect, useState } from "react";

import { Divider, FormControlLabel, Stack, Switch } from "@mui/material";

import { useTranslation } from "react-i18next";

import { Agg } from "system/query/Aggs";
import LeafAggSelector from "components/widgetParts/LeafAggSelector";
import { QueryField, QueryFieldOption, QueryInfo } from "system/query/QueryInfo";
import SettingsToggleButton, { horizontalAlignmentOptions } from "components/settingsConfig/uiComponents/SettingsToggleButton";
import { SimpleFormLabel } from "components/dialogs/SimpleFormDialog";
import TabbedDialog from "components/dialogs/TabbedDialog";
import SplitSettingsWrapper from "components/settingsConfig/SplitSettingsWrapper";
import SettingsOptions from "components/settingsConfig/SettingsOptions";
import SettingsAccordion from "components/settingsConfig/uiComponents/SettingsAccordion";
import SettingsColorPicker from "components/settingsConfig/uiComponents/SettingsColorPicker";
import SettingsWidgetTitleSelect from "components/settingsConfig/uiComponents/SettingsWidgetTitleSingleSelect";
import SettingsTextField from "components/settingsConfig/uiComponents/SettingsTextField";
import ArcChartPreview from "./ArcChartPreview";
import {AutoWidgetQueryBehavior, getSingleOriginalRequestByName, WidgetRequest } from "system/widget/Widget";
import { WidgetSettingsProps } from "components/widgetParts/WidgetSettingsWrapper";
import QueryWidgetSettingsMissingQueryInfoAlert from "components/widgetParts/queryWidgetComposition/QueryWidgetSettingsMissingQueryInfoAlert";

export const defaultArcChartRequest = {
  name: 'arc-chart',
  endpoint: 'query/agg',
  queryParams: {
    flatten: true,
    include_total: true
  },
  autoWidgetQueryBehavior: AutoWidgetQueryBehavior.AUTO_SINGLE,
} as WidgetRequest;

export function BuildDefaultSinglePercentageValueRequestsFunction(defaultWidgetRequest:WidgetRequest): (queryInfo: QueryInfo) => WidgetRequest[] | undefined {
  return function BuildDefaultSinglePercentageValueRequests(queryInfo: QueryInfo): WidgetRequest[] | undefined {
    const widgetRequest: WidgetRequest = { ...defaultWidgetRequest };

    let firstOption : QueryFieldOption | undefined = undefined;
    let foundKeywordField = queryInfo.fields?.find((field: QueryField) => {
      if (field.id === "sentiment") {
        firstOption = field.options?.find((option: QueryFieldOption) => option.id === "p");
        return true;
      }
    }) || false;

    if (!foundKeywordField) {
      foundKeywordField = queryInfo.fields?.find((field: QueryField) => {
        const numberOfOptions = parseInt(field?.options?.length + "");
        return ["keyword", "keywords"].includes(field.type) && numberOfOptions;
      }) || false;
    }

    if (foundKeywordField) {
      const requestInitBody = { aggs: {} };

      firstOption = firstOption || foundKeywordField?.options?.[0];
      if (firstOption && firstOption.id && firstOption.label) {
        requestInitBody.aggs = {
          [foundKeywordField.label]: {
            "op":"keyword",
            "field": foundKeywordField.id,
            "filter": [firstOption.id],
            "aggs": {
              "%": {
                "op": "percent",
                "of_total": true
              }
            }
          }
        };

        widgetRequest.requestInit = { body: requestInitBody };
        return [widgetRequest];
      }
    }
    return;
  }
}

export type ArcChartSettingsStateType = {
  fillColor?: string;
  customWidgetTitle: string;
  widgetTitle: string;
  widgetTitleAlignment: string;
}

export const defaultSettings: ArcChartSettingsStateType = {
  customWidgetTitle: '',
  widgetTitle: 'default',
  widgetTitleAlignment: 'left',
}

export default function ArcChartSettings(props: WidgetSettingsProps): JSX.Element {
  const { i18n } = useTranslation();

  const [aggsLabel, setAggsLabel] = useState<string>("");
  const [localAggs, setLocalAggs] = useState<Agg>();
  const [includeOnlyRowsWithKeyword, setIncludeOnlyRowsWithKeyword] = useState<boolean>(!!props.widget?.request_info?.requests?.[0]?.requestInit?.body?.query);

  const updatedDefaultSettings = {...defaultSettings, ...props?.widget?.data?.settings};
  const [settingsState, setSettingsState] = useState<ArcChartSettingsStateType>(updatedDefaultSettings);
  const settingsOnChange = (updatedState: ArcChartSettingsStateType) => {
    setSettingsState(updatedState);
  };

  const [tabValue, setTabValue] = useState<number>(0);
  const handleTabChange = (event: React.SyntheticEvent<Element, Event>, newValue: number) => setTabValue(newValue);

  useEffect(() => {
    const originalRequest = getSingleOriginalRequestByName(props.activeWidgetRequests, 'arc-chart');
    const requestBody = originalRequest?.requestInit?.body || {};

    const keys = Object.keys(requestBody?.aggs || {});
    if (keys.length > 0) {
      const firstKey = keys[0];
      setAggsLabel(firstKey);
      setLocalAggs(requestBody?.aggs[firstKey]);
      setIncludeOnlyRowsWithKeyword(!requestBody?.aggs[firstKey]?.include_null);
    }
  }, [props.widget?.request_info?.requests, props.widget?.request_info?.requests?.length]);

  const updateAggsAndData = () => {
    const label:string = aggsLabel || localAggs?.field || "_total";

    const widgetRequest: WidgetRequest = { ...defaultArcChartRequest };
    let requestInitBody = { aggs: {} };

    // check if the field is a boolean type, if so we need to remove the sub aggs
    const selectedField: QueryField | undefined = props.queryInfo?.fields?.find((field: QueryField) => field.id === localAggs?.field);
    if (localAggs) {
      if (selectedField?.type !== "boolean") {
          localAggs.aggs = {
            "%": {
              "op": "percent",
              "of_total": true
            }
          }
          localAggs.include_null = !includeOnlyRowsWithKeyword;
          requestInitBody.aggs = { [label]: localAggs };
      } else {
        delete localAggs?.aggs;
        localAggs.op = "keyword";
        requestInitBody.aggs = { [label]: localAggs };
      }
    } else {
      requestInitBody = { aggs: {} };
    }
    widgetRequest.requestInit = { body: requestInitBody };

    if (props.widget?.id && props.saveWidgetDataAndRequests) {
      props.saveWidgetDataAndRequests({ settings: settingsState }, [widgetRequest]).then(() => {
        if (props.widgetRefresh) {
          props.widgetRefresh();
        }
      });
    }
  }

  return (
    <TabbedDialog
      currentTab={tabValue}
      handleTabChange={handleTabChange}
      onCancel={() => props.setSettingsDialogIsOpen(false)}
      onConfirm={() => {
        updateAggsAndData();
        props.setSettingsDialogIsOpen(false);
      }}
      open={true}
      tabs={[i18n.t("widget-settings.query-and-data"), i18n.t("display")]}
      title={i18n.t("widget-types.arc-chart.settings.title")}>
      <>
        {/* Tab #1 */}
        {tabValue === 0 && <>
          {props.queryInfo && <>
            <SimpleFormLabel>{i18n.t("category")}</SimpleFormLabel>
            <LeafAggSelector
              allowedFieldTypes={["keyword", "keywords", "boolean"]}
              queryInfo={props.queryInfo}
              aggs={localAggs}
              setAggs={(newAggs: Agg | null) => setLocalAggs(newAggs)}
              aggsLabel={aggsLabel}
              setAggsLabel={setAggsLabel}
              ></LeafAggSelector>
              <br/>
              {localAggs?.field &&
                <FormControlLabel
                  control={
                    <Switch
                      checked={includeOnlyRowsWithKeyword}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        setIncludeOnlyRowsWithKeyword(event.target.checked);
                      }}/>
                  }
                  label={i18n.t("requestOptions.only-rows-with-value")}
                  />
              }
          </>}
          {(!props.queryInfo) &&
            <QueryWidgetSettingsMissingQueryInfoAlert />
          }
        </>}
        {/* Tab #2 */}
        {tabValue === 1 &&
          <SplitSettingsWrapper>
            {/* Widget Graph Preview */}
            <ArcChartPreview settingsState={settingsState} />

            {/* Settings Options */}
            <SettingsOptions onChange={settingsOnChange} settingsState={settingsState}>
              <SettingsAccordion label={i18n.t("widget-settings.widget-title")} showBottomDivider={true}>
                <SettingsWidgetTitleSelect
                  keyid="widgetTitle"
                  label={i18n.t("title")}
                  showQueryOption={true}
                  widgetTitleValue={settingsState.widgetTitle} />
                {settingsState.widgetTitle === 'custom' &&
                  <Stack direction="row" spacing={2} sx={{ marginLeft: 2 }}>
                    <Divider flexItem orientation="vertical" sx={{ width: "5px" }} />
                    <SettingsTextField
                      keyid="customWidgetTitle"
                      label={i18n.t("widget-settings.custom-text")}
                      value={settingsState.customWidgetTitle}
                    />
                  </Stack>}
                <SettingsToggleButton
                  keyid="widgetTitleAlignment"
                  label={i18n.t("widget-settings.alignment")}
                  optionsList={horizontalAlignmentOptions}
                  value={settingsState.widgetTitleAlignment} />
              </SettingsAccordion>
              <SettingsColorPicker
                color={settingsState.fillColor ?? undefined}
                defaultColor={undefined}
                keyid="fillColor"
                label={i18n.t("color")} />
            </SettingsOptions>
          </SplitSettingsWrapper>}
      </>
    </TabbedDialog>
  )
}